import { FC, ReactElement, useContext, useEffect, useState } from "react"
import { ZipProjectContext } from "@app/projects/zip-projects/ZipProjectContext"
import { ProjectRunConfigurationStatus } from "@app/projects/enumerators";
import { isProjectRunning, onRunProject } from "@app/projects/zip-projects/utility";
import { translateProjectChangeReason } from "@app/projects/projectChangeReason";
import FlyoutLink from "@app/shared/Flyouts/FlyoutLink";
import { ZipProjectPublishFlyout } from "@app/projects/zip-projects/ZipProjectPublishFlyout";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import PendingUnprocessedSpecialtyGroupExceptionsChangesSidePanelLink from "@app/projects/servicing-provider/PendingUnprocessedSpecialtyGroupExceptionsChangesSidePanelLink";
import { getProviderChangeRoleGate } from "@app/projects/providerChangeRoleGate";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESAlertsContainer } from "@app/shared/qes-standards/AlertsContainer";
import { QESButton } from "@app/shared/qes-standards/Button";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import ProviderActionsDrawerFlyout from "../network-assessment/provider-actions/ProviderActionsDrawerFlyout";

const runningBanner = (
  <QESAlert type="info" key="projectIsRunning" persist={true}>
    This project is in a read-only mode while processing updates. Some items may not appear while they are being worked on.
  </QESAlert>
);

export const ZipProjectAlertBanners: FC = () => {
  const projectContext = useContext(ZipProjectContext);
  const user = useContext(UserContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const { state, dispatch } = projectContext;
  const [alertBanners, setAlertBanners] = useState<ReactElement[]>([]);
  const projectIsRunning = isProjectRunning(state.project);

  const renderProviderActionsFlyoutLink = () => {
    return (
      <ProviderActionsDrawerFlyout
        classNames="ml10 qes-button-secondary provider-actions-link"
        resetBanner={async (hasUnprocessedTerminations, hasUnprocessedTargets, hasUnprocessedExcludes) =>
          projectContext.onUpdateUnprocessedProviders(hasUnprocessedTerminations, hasUnprocessedTargets, hasUnprocessedExcludes)}
        testId="zip-project-alert-banners__provider-actions-flyout-link">
        View and Process
      </ProviderActionsDrawerFlyout>
    );
  }

  // all status/alert banner checks should be done in this useEffect
  // (or an abstracted function it calls) so we only set them in one place
  useEffect(() => {
    if (!state.project) {
      return;
    }
    const isShared = state.project.resource.isShared;
    const newAlertBanners: ReactElement[] = [];

    const isRoleAuthorizationEnforced = featureFlags.has(Features.ENFORCE_PROVIDER_CHANGE_ROLE_AUTHORIZATION);
    const termAdmin = user.roles.includes(Roles.TERM_PROVIDER_ADMIN) && featureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION);
    const projectionProcessor = (isRoleAuthorizationEnforced
      ? getProviderChangeRoleGate(user.roles, {
        hasExcludes: state.project.hasUnprocessedExcludedProviders,
        hasTargets: state.project.hasUnprocessedTargetProviders,
        hasTerminations: false,
      }).hasRequiredRoles
      : user.roles.includes(Roles.PROJECTION_PROCESSOR)) && featureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING);
    const showTermMessage = termAdmin && state.project.hasUnprocessedProviderTerminations && !state.project.isRunning && state.runConfiguration && state.runConfiguration.status !== ProjectRunConfigurationStatus.Failed;
    const showProjectionMessage = projectionProcessor && (state.project.hasUnprocessedTargetProviders || state.project.hasUnprocessedExcludedProviders) && state.runConfiguration && state.runConfiguration.status !== ProjectRunConfigurationStatus.Failed;
    const renderProviderActionsFlyoutLinkElement = renderProviderActionsFlyoutLink();

    const showExceptionBanner = user.roles.includes(Roles.EXCEPTION_MANAGER)
      && featureFlags.has(Features.ZIP_EXCEPTIONS)
      && state.project.hasUnprocessedSpecialtyGroupExceptions
      && !!state.runConfiguration
      && state.runConfiguration.status !== ProjectRunConfigurationStatus.Failed;

    if (projectIsRunning) {
      newAlertBanners.push(runningBanner);
    } else if (!isShared) {
      if (!state.runConfiguration) {
        newAlertBanners.push(
          <QESAlert key="created" type="info" persist={true}>
            <strong>Thank you!</strong> Your project has been created, please run it.
            <QESButton
              className="ms-3"
              qesType="secondary"
              onClick={() => { onRunProject(dispatch, projectContext.onRunProject, projectContext.state.project.resource.resourceId); }}
              qesTestId="zip-project-overview__run-project--no-config"><i className="fa fa-rocket" /> Run Now</QESButton>
          </QESAlert>
        );
      }

      if (state.runConfiguration?.status === ProjectRunConfigurationStatus.Succeeded
        && state.project.projectChangeReasons.length > 0) {
        const list = state.project.projectChangeReasons.map((reason) => (
          <li key={reason}>{translateProjectChangeReason(reason)}</li>
        ));
        newAlertBanners.push(
          <QESAlert
            type="warning"
            key="projectChangeReasons"
            persist={true}
            collapseTitle={
              <>
                <strong>Warning!</strong> Your project results are out of date.
                {/* HTML Syntax doesn't allow a button inside a button (QESAlert's collapse button),
                      so we get around it here with a span with role="button" and the properties that otherwise would
                      be native to a button (onClick, onKeyDown, tabIndex) 
                      See https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role for more
                  */}
                <span
                  className="d-inline-block ms-3"
                  onClick={() => onRunProject(dispatch, projectContext.onRunProject, projectContext.state.project.resource.resourceId)}
                  onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); onRunProject(dispatch, projectContext.onRunProject, projectContext.state.project.resource.resourceId); } }}
                  role="button"
                  tabIndex={0}
                  data-testid="zip-project-overview__run-project--results-out-of-date"
                >
                  <i className="fa fa-rocket" /> Run Now
                </span>
              </>
            }
          >
            <p className="pt5">The following changes have occurred since your project was last run:</p>
            <ul>
              {list}
            </ul>
          </QESAlert>
        )
      }

      if (state.project.canPublish && state.runConfiguration?.datePublished === undefined) {
        newAlertBanners.push(
          <QESAlert type="warning" key="projectUnpublished" persist={true}>
            <strong>Warning!</strong> Your project results are unpublished.
            <FlyoutLink
              classNames="ms-3"
              content={{
                body: <ZipProjectPublishFlyout onPublish={projectContext.onPublishTimeSlice} />,
                title: "Publish Options",
              }}
              testId="publish-project-flyout-link"
              disabled={state.loading}
              size="40"
            >
              <i className="fa fa-thumbs-o-up pr5" /> Publish
            </FlyoutLink>
          </QESAlert>
        );
      }

      if (state.runConfiguration?.status === ProjectRunConfigurationStatus.Failed
        && !state.project.isRunning) {
        if (state.project.hasFailedProviderChanges && projectionProcessor) {
          newAlertBanners.push(
            <QESAlert type="danger" persist={true} key="failedProviderChanges">
              {isShared
                ? <>An error occurred during processing. We apologize for the inconvenience. Please contact your administrator.</>
                : <>Project failed while processing provider changes. If you are not sure you should reprocess this project, submit a ticket to QES Intake.
                  <QESButton className="ms-3" qesType="secondary" onClick={() => projectContext.onProcessProviderChanges(true)} qesTestId="zip-project-overview__process-provider-changes">
                    <QesIcon iconName={FontAwesome.Rocket} width="14" height="16" style={{ paddingBottom: "2px" }} isDecorative />Re-Process Provider Changes
                  </QESButton>
                </>}
            </QESAlert>
          );
        } else {
          newAlertBanners.push(
            <QESAlert type="warning" key="projectFailed" persist={true}>
              <strong>Warning!</strong> Your latest project run failed.
              <QESButton className="ms-3" qesType="secondary" onClick={() => onRunProject(dispatch, projectContext.onRunProject, projectContext.state.project.projectId)} qesTestId="zip-project-overview__run-project--run-failed"><i className="fa fa-rocket" /> Run Again</QESButton>
            </QESAlert>
          );
        }
      }

      if (showTermMessage && showProjectionMessage) {
        newAlertBanners.push(
          <QESAlert type="warning" persist={true}>
            <strong>Warning!</strong> Your project has unprocessed provider terminations and unprocessed targeted or excluded providers. Process your pending actions to view the most up-to-date project data.
            {renderProviderActionsFlyoutLinkElement}
          </QESAlert>
        );
      } else if (showTermMessage) {
        newAlertBanners.push(
          <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]} key="unprocessedTerminations">
            <QESAlert type="warning" persist={true}>
              <strong>Warning!</strong> Your project has unprocessed provider terminations. Process your pending actions to view the most up-to-date project data.
              {renderProviderActionsFlyoutLinkElement}
            </QESAlert>
          </RoleRestricted>
        );
      } else if (showProjectionMessage) {
        const projectionAlert = (
          <QESAlert type="warning" persist={true} key="unprocessedProjections">
            <strong>Warning!</strong> Your project has unprocessed targeted or excluded providers. Process your pending actions to view the most up-to-date project data.
            {renderProviderActionsFlyoutLinkElement}
          </QESAlert>
        );

        // When role authorization is not enforced, keep the pre-existing TERM_PROVIDER_ADMIN restriction
        // on this banner so behavior matches the released build.
        newAlertBanners.push(
          isRoleAuthorizationEnforced
            ? projectionAlert
            : (
              <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]} key="unprocessedProjections">
                {projectionAlert}
              </RoleRestricted>
            )
        );
      }

      if (showExceptionBanner) {
        newAlertBanners.push(
          <QESAlert type="warning" key="unprocessed-exceptions" persist={true}>
            <strong> Warning!</strong> Your project has unprocessed specialty group exceptions.
            <PendingUnprocessedSpecialtyGroupExceptionsChangesSidePanelLink
              isZipProject={true}
              onSubmit={() => projectContext.onProcessSpecialtyExceptions(structuredClone(state.project), structuredClone(state.runConfiguration))}
              projectId={state.project.projectId}
            />
          </QESAlert>
        );
      }
    }

    setAlertBanners(newAlertBanners);
  }, [state.runConfiguration, user, state.project, featureFlags]);

  return (
    alertBanners.length > 1
      ? (
        <QESAlertsContainer containerClass="mb-3">
          {alertBanners}
        </QESAlertsContainer>
      )
      : <>{alertBanners}</>
  )
}